import { useMemo } from 'react';
import { ClubInfo, Stadium, Contacts } from './components';
import ArticleListUI from '../../../article/components/ArticleList/ArticleListUI';
import { LinkSize, LinkVariant } from '../../../common/components/Link/contracts';
import NewsletterSubscribe from '../../../article/components/NewsletterSubscribe/NewsletterSubscribe';
import Link from '../../../common/components/Link/Link';
import { ArticleListVariant } from '../../../article/components/ArticleList/contracts';
import LeagueTableUI from '../../../league/components/LeagueTable/LeagueTableUI';
import FixtureListUI from '../../../fixtures/components/FixtureList/FixtureListUI';
import { LeagueTableColumn } from '../../../league/components/LeagueTable/leagueTableColumns';
import { ClubNameType } from '../../../fixtures';
import { Image } from '../../../common/components/Image';
import { SponsorsGrid } from '@ui/league/SponsorsGrid';
import { Article, ArticleVariant, Club, LeagueTable as LeagueTableApi, Match, PaginatedResponse } from '@repo/types';
import { Tabs } from '@ui/tabs/Tabs/Tabs';
import { TabItem } from '@ui/tabs/Tabs';

export type ClubOverviewProps = {
  isMobile: boolean;
  club: Club;
  articles: PaginatedResponse<Article> | undefined;
  leagueTable: LeagueTableApi | undefined;
  results: PaginatedResponse<Match> | undefined;
  fixtures: PaginatedResponse<Match> | undefined;
};

const tableColumns = [LeagueTableColumn.Rank, LeagueTableColumn.Team, LeagueTableColumn.P];

export function ClubOverview({ isMobile, club, articles, leagueTable, results, fixtures }: ClubOverviewProps) {
  const divisionName = leagueTable && leagueTable.competition.name.split('-')[1];
  const firstTeam = club.teams?.find((team) => team.isDefault);

  const tabsToRender = useMemo(() => {
    const tabs: TabItem[] = [];
    if (leagueTable) {
      tabs.push({
        id: 'league-table',
        label: 'Table',
        children: (
          <LeagueTableUI
            highlightedClubId={club.id}
            leagueTable={leagueTable}
            columns={tableColumns}
            className={'team-overview__league-table'}
            limit={isMobile ? 5 : 5}
            nameType={ClubNameType.ShortName}
            linkLabel="FULL TABLE"
            linkUrl={leagueTable.competition.slug}
            linkVariant={LinkVariant.Contained}
            linkSize={LinkSize.m}
          />
        ),
      });
    }
    if (fixtures) {
      tabs.push({
        id: 'fixtures',
        label: 'Fixtures',
        children: (
          <FixtureListUI
            fixtures={fixtures.items}
            groupByDate={true}
            nameType={ClubNameType.ShortName}
            linkUrl={`/fixtures?team=${firstTeam?.id}`}
            linkLabel="VIEW ALL"
            linkVariant={LinkVariant.Contained}
            sortByDate="ASC"
          />
        ),
      });
    }
    if (results) {
      tabs.push({
        id: 'results',
        label: 'Results',
        children: (
          <FixtureListUI
            fixtures={results.items}
            groupByDate={true}
            nameType={ClubNameType.ShortName}
            linkUrl={`/results?team=${firstTeam?.id}`}
            linkLabel="VIEW ALL"
            linkVariant={LinkVariant.Contained}
            sortByDate="DESC"
          />
        ),
      });
    }
    return tabs;
  }, [club.id, firstTeam?.id, fixtures, isMobile, leagueTable, results]);

  return (
    <div className="team-overview">
      <div className="team-overview__content">
        <div className="team-overview__name-logo-container">
          {club.logo && (
            <div className="team-overview__logo">
              <Image src={club.logo} alt={club.fullName} priority fill />
            </div>
          )}
          <div className={'team-overview__team-name-container'}>
            {divisionName && <span className="team-overview__division-name">{divisionName}</span>}
            <h1 className={'team-overview__team-name'}>{club.fullName}</h1>
            {club.nickName && <h3 className={'team-overview__team-nickname'}>{club.nickName}</h3>}
          </div>
        </div>

        {isMobile ? (
          <div className="team-overview__stat-tabs-container">
            <Tabs tabs={tabsToRender} />
          </div>
        ) : (
          <div className="slc-row">
            {leagueTable && (
              <div
                className="slc-column"
                style={{
                  display: 'flex',
                  gridColumn: isMobile ? 'span 12' : 'span 4',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',
                  padding: `var(--spacing-6) var(--spacing-3) var(--spacing-6) var(--spacing-3)`,
                }}
              >
                <h3 className="team-overview__stats-title">{divisionName}</h3>
                <LeagueTableUI
                  highlightedClubId={club.id}
                  leagueTable={leagueTable}
                  columns={tableColumns}
                  className={'team-overview__league-table'}
                  limit={isMobile ? 5 : 5}
                  nameType={ClubNameType.ShortName}
                  linkLabel="FULL TABLE"
                  linkUrl={leagueTable.competition.slug}
                  linkVariant={LinkVariant.Contained}
                  linkSize={LinkSize.m}
                />
              </div>
            )}
            {fixtures && fixtures.items.length > 0 && (
              <div
                className="slc-column"
                style={{
                  display: 'flex',
                  gridColumn: isMobile ? 'span 12' : 'span 4',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',
                  padding: `var(--spacing-6) var(--spacing-3) var(--spacing-6) var(--spacing-3)`,
                }}
              >
                <h3 className="team-overview__stats-title">Upcoming fixtures</h3>
                <FixtureListUI
                  fixtures={fixtures.items}
                  groupByDate={true}
                  nameType={ClubNameType.ShortName}
                  linkUrl={`/fixtures?team=${firstTeam?.id}`}
                  linkLabel="VIEW ALL"
                  linkVariant={LinkVariant.Contained}
                  sortByDate="ASC"
                />
              </div>
            )}
            {results && results.items.length > 0 && (
              <div
                className="slc-column"
                style={{
                  display: 'flex',
                  gridColumn: isMobile ? 'span 12' : 'span 4',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',
                  padding: `var(--spacing-6) var(--spacing-3) var(--spacing-6) var(--spacing-3)`,
                }}
              >
                <h3 className="team-overview__stats-title">Recent results</h3>
                <FixtureListUI
                  fixtures={results.items}
                  groupByDate={true}
                  nameType={ClubNameType.ShortName}
                  linkUrl={`/results?team=${firstTeam?.id}`}
                  linkLabel="VIEW ALL"
                  linkVariant={LinkVariant.Contained}
                  sortByDate="DESC"
                />
              </div>
            )}
          </div>
        )}

        {articles && articles?.items?.length > 0 && (
          <div className="team-overview__article-list-container">
            <div className={'team-overview__more-articles-btn-container'}>
              <h3>Latest {club.fullName} News</h3>
              <Link
                url={`/news?tags=${club.slug}`}
                label={`More ${club.shortName} news`}
                openInNewTab={false}
                variant={LinkVariant.Text}
                size={isMobile ? LinkSize.sm : LinkSize.m}
              />
            </div>
            <ArticleListUI
              paginatedArticles={articles}
              listVariant={isMobile ? ArticleListVariant.Default : ArticleListVariant.TwoColumnGrid}
              variant={isMobile ? ArticleVariant.TextSideBySide : ArticleVariant.Default}
              showDate={!isMobile}
              columns={isMobile ? 1 : 4}
            />
          </div>
        )}

        <div className="team-overview__stat-tabs-container">
          <Tabs
            tabs={[
              {
                id: 'home-venue',
                label: 'Home Venue',
                children: <Stadium club={club} isMobile={isMobile} />,
              },
              {
                id: 'club-info',
                label: 'Club Info',
                children: <ClubInfo club={club} />,
              },
              {
                id: 'contacts',
                label: 'Contacts',
                children: <Contacts club={club} />,
              },
            ]}
          />
        </div>
        <div className="team-overview__newsletter-container">
          <NewsletterSubscribe title={'Get the latest news straight to your inbox'} />
        </div>
        <SponsorsGrid
          className="matches-list__sponsors-grid"
          title="Our Trusted Partners"
          showAllSponsors
          showImage
          columns={isMobile ? 3 : 5}
          showName={false}
          showDescription={false}
        />
      </div>
    </div>
  );
}
